import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  Tooltip,
  IconButton,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import type { 
  CreateActivityTypeRequest, 
  OperationType, 
  ActivityTypeRequiredFields,
  OperationTypeConfig 
} from '../../../types/activityType';
import { operationTypeService } from '../../../services/operationTypeService';

interface CreateActivityTypeProps {
  open: boolean;
  companyId: number;
  onClose: () => void;
  onSubmit: (activityTypeData: CreateActivityTypeRequest) => Promise<void>;
}

export default function CreateActivityType({
  open,
  companyId,
  onClose,
  onSubmit,
}: CreateActivityTypeProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [name, setName] = useState('');
  const [operationType, setOperationType] = useState<OperationType>('STOCK_IN');
  const [requiredFields, setRequiredFields] = useState<ActivityTypeRequiredFields>({
    clientRequired: false,
    providerRequired: false,
    stockRequired: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // State for operation types configuration
  const [operationTypesMap, setOperationTypesMap] = useState<Map<OperationType, OperationTypeConfig>>(new Map());
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  // Load operation types configuration when dialog opens
  useEffect(() => {
    if (open && operationTypesMap.size === 0) {
      loadOperationTypesConfig();
    }
  }, [open, operationTypesMap.size]);

  // Update required fields when operation type changes
  useEffect(() => {
    const selectedConfig = operationTypesMap.get(operationType);
    if (selectedConfig) {
      const newRequiredFields: ActivityTypeRequiredFields = {
        clientRequired: false,
        providerRequired: false,
        stockRequired: true
      };

      selectedConfig.requiredFields.forEach(field => {
        const fieldName = field.name.toLowerCase();
        if (fieldName === 'client') {
          newRequiredFields.clientRequired = field.isRequiredByDefault;
        } else if (fieldName === 'provider') {
          newRequiredFields.providerRequired = field.isRequiredByDefault;
        } else if (fieldName === 'stock') {
          newRequiredFields.stockRequired = field.isRequiredByDefault;
        }
      });

      setRequiredFields(newRequiredFields);
    }
  }, [operationType, operationTypesMap]);

  const loadOperationTypesConfig = async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const config = await operationTypeService.getOperationTypesConfig();
      
      // Create map for quick lookup
      const configMap = new Map<OperationType, OperationTypeConfig>();
      config.operationTypes.forEach(opType => {
        configMap.set(opType.name, opType);
      });
      setOperationTypesMap(configMap);
    } catch (err) {
      console.error('Error loading operation types config:', err);
      setConfigError('Не вдалося завантажити конфігурацію типів операцій');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Назва типу активності обов\'язкова');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit({
        companyId,
        name: name.trim(),
        operationType,
        requiredFields,
      });
      
      // Reset form and close dialog on success
      setName('');
      setOperationType('STOCK_IN');
      setRequiredFields({
        clientRequired: false,
        providerRequired: false,
        stockRequired: true
      });
      onClose();
    } catch (err) {
      console.error('Error creating activity type:', err);
      setError('Не вдалося створити тип активності. Спробуйте ще раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setName('');
      setOperationType('STOCK_IN');
      setRequiredFields({
        clientRequired: false,
        providerRequired: false,
        stockRequired: true
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '100vw',
          overflowX: 'hidden',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Створити новий тип активності</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, overflowX: 'hidden' }}>
            {configError && (
              <Alert 
                severity="error" 
                action={
                  <Button color="inherit" size="small" onClick={loadOperationTypesConfig}>
                    Повторити
                  </Button>
                }
              >
                {configError}
              </Alert>
            )}
            
            <TextField
              autoFocus
              label="Назва типу активності"
              fullWidth
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              error={!!error}
              helperText={error}
              disabled={loading}
              required
            />

            <FormControl fullWidth required sx={{ minWidth: 0 }}>
              <InputLabel>Тип операції</InputLabel>
              <Select
                value={operationType}
                label="Тип операції"
                onChange={(e) => setOperationType(e.target.value as OperationType)}
                disabled={loading || configLoading}
                sx={{
                  '& .MuiSelect-select': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                {configLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Завантаження...
                  </MenuItem>
                ) : operationTypesMap.size > 0 ? (
                  Array.from(operationTypesMap.values()).map((opType) => (
                    <MenuItem key={opType.name} value={opType.name}>
                      {opType.description}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>Немає доступних типів операцій</MenuItem>
                )}
              </Select>
              {configError && (
                <FormHelperText error>{configError}</FormHelperText>
              )}
              {!configError && !configLoading && (
                <FormHelperText>
                  Виберіть тип операції зі складськими запасами
                </FormHelperText>
              )}
            </FormControl>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Обов'язкові поля для цього типу активності
              </Typography>
              <Tooltip 
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Налаштування обов'язкових полів
                    </Typography>
                    <Typography variant="body2">
                      Оберіть, які поля будуть обов'язковими для цього типу активності.
                      Деякі поля можуть бути заблоковані залежно від типу операції.
                    </Typography>
                  </Box>
                }
                arrow
                placement="right"
              >
                <IconButton size="small" sx={{ p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={requiredFields.clientRequired}
                    onChange={(e) => setRequiredFields(prev => ({ ...prev, clientRequired: e.target.checked }))}
                    disabled={loading}
                  />
                }
                label="Клієнт обов'язковий"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={requiredFields.providerRequired}
                    onChange={(e) => setRequiredFields(prev => ({ ...prev, providerRequired: e.target.checked }))}
                    disabled={loading}
                  />
                }
                label="Постачальник обов'язковий"
              />
              {operationType !== 'BETWEEN_STOCKS' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={requiredFields.stockRequired}
                      onChange={(e) => setRequiredFields(prev => ({ ...prev, stockRequired: e.target.checked }))}
                      disabled={loading}
                    />
                  }
                  label="Склад обов'язковий"
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Скасувати
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Створення...' : 'Створити'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
